<template>
  <div>
    <div class="contact_title">
      <h2>
        {{ $t('deposit.default.deposit') }}
        <span v-if="isFromDepositFundsCps"> - (Special Page)</span>
      </h2>
      <p></p>
    </div>
    <div class="box_card_top" :class="{ 'mobile-view': isMultiplePaymentTypes && paymentTypes.length > 2 }">
      <div class="form_deposit" v-if="paymentDetails.merchant_variable">
        <h4>{{ descriptionTitle }}</h4>
        <div class="info_box_choose" v-if="isMultiplePaymentTypes">
          <el-tooltip
            v-for="(item, index) in paymentTypes"
            class="item"
            effect="dark"
            :content="descriptionTitle"
            placement="bottom-start"
            :disabled="getLabelSwitch(item.merchant_variable)"
            :key="item.index"
          >
            <el-button
              @click="handleSelectIndex(index)"
              :class="elButtonClassName(activeIndex === index)"
              :disabled="!getLabelSwitch(item.merchant_variable)"
            >
              <span>{{ descriptionTitle }}</span>
            </el-button>
          </el-tooltip>
        </div>
        <div class="info_box">
          <p>
            {{
              $t('deposit.method.desc', {
                method: descriptionTitle
              })
            }}
          </p>
          <ul>
            <li v-for="(item, index) in instData" :key="index">
              <span v-html="item"></span>
            </li>
          </ul>
        </div>
        <div class="img">
          <img
            :src="paymentLogo(paymentDetails.payment_method, paymentDetails.actual_currency_code, countryCode)"
            alt="logo"
          />
        </div>
      </div>
    </div>
    <div class="box_card_bottom">
      <div class="form_main">
        <el-form :model="cpsForm" ref="cpsForm" :rules="cpsRules">
          <ul class="clearfix">
            <li class="fl">
              <AccountNumber
                :supportedCurrenciesList="tradeAccountCurrencies"
                :disabled="isDisabled"
                @setCurrency="setCurrency"
                @setAccountNumber="setAccountNumber"
              ></AccountNumber>
            </li>
            <li class="fr">
              <numeric-input
                v-model="cpsForm.amount"
                :currency="accountCurrency"
                :precision="2"
                :label="$t('common.field.amt')"
                :readOnly="isDisabled"
                name="amount"
                @blur="onAmountBlur"
              ></numeric-input>
            </li>
          </ul>

          <ul class="clearfix" v-if="bankCurrencySupport">
            <li class="fl">
              <DepositCurrency
                :supportedCurrencies="supportedDepositCurrencies"
                :isDepositCurrency="false"
                :defaultCurrency="cpsForm.depositCurrency"
                @setDepositCurrency="setDepositCurrency"
              ></DepositCurrency>
            </li>
          </ul>

          <ul class="clearfix">
            <template v-if="cpsForm.attachVariables.length > 0">
              <li
                :class="index % 2 == 0 ? 'fl' : 'fr'"
                v-for="(type, index) in cpsForm.attachVariables"
                :key="type.key"
              >
                <!-- Email Field -->
                <InputForm
                  :label="type.field_name"
                  :name="'attachVariables.' + index + '.value'"
                  :testId="type.type"
                  v-model="cpsForm.attachVariables[index].value"
                  :disabled="isDisabled"
                  v-if="type.field_type == 'input_field' && type.type == 'email'"
                  rules="email"
                ></InputForm>

                <!-- Input Field -->
                <DynamicRestrictInput
                  :label="type.field_name"
                  :name="'attachVariables.' + index + '.value'"
                  :testId="type.type"
                  :disabled="isDisabled"
                  :restrictionType="type.restriction_type"
                  v-model="cpsForm.attachVariables[index].value"
                  :maxlength="type.field_length"
                  v-else-if="type.field_type == 'input_field'"
                >
                </DynamicRestrictInput>

                <!-- Dropdown List -->
                <SelectForm
                  :label="type.field_name"
                  :name="'attachVariables.' + index + '.value'"
                  :testId="type.key"
                  :disabled="isDisabled"
                  v-model="cpsForm.attachVariables[index].value"
                  v-if="type.field_type == 'dropdown_list' && type.type == 'depositor'"
                  rules="depositor"
                >
                  <el-option
                    v-for="(value, name) in type.field_content"
                    :label="value.desc"
                    :value="value.value"
                    :key="name"
                  ></el-option>
                </SelectForm>

                <SelectForm
                  :label="type.field_name"
                  :name="'attachVariables.' + index + '.value'"
                  :testId="type.key"
                  :disabled="isDisabled"
                  v-model="cpsForm.attachVariables[index].value"
                  v-if="type.field_type == 'dropdown_list' && type.type == 'bank'"
                  rules="depositor"
                >
                  <el-option
                    v-for="(value, name) in type.field_content"
                    :label="value.bank_name"
                    :value="value.bank_code"
                    :key="name"
                  ></el-option>
                </SelectForm>
              </li>
            </template>
            <li :class="getFieldLocation(cpsForm.attachVariables)">
              <InputForm
                :label="$t('common.field.imptNotes')"
                :disabled="isDisabled"
                name="applicationNotes"
                v-model="cpsForm.notes"
              >
              </InputForm>
            </li>
            <li v-if="isEntitleUSDTDepositCashback" :class="cpsForm.attachVariables.length % 2 == 0 ? 'fr' : 'fl'">
              <DepositCashbackTag text="CASH BACK BONUS" />
            </li>
          </ul>

          <ul class="clearfix">
            <li class="fl" v-if="showExchangeRates">
              <el-form-item>
                <p class="mb-2">
                  <span class="required_icon">*</span>
                  {{
                    $t('deposit.default.rate.rate', {
                      oldCurrency: accountCurrency,
                      newCurrency: newCurrencyRate
                    })
                  }}
                  <span>{{ rate }}</span>
                </p>
                <p class="mb-2">
                  <span class="required_icon">*</span>
                  {{
                    bankCurrencySupport
                      ? this.cpsForm.depositCurrency
                      : accountCurrency === 'USC' && this.paymentDetails.unique_currency
                      ? 'USD'
                      : paymentDetails.currency_code
                  }}:
                  <span>{{ rateChange }}</span>
                </p>
              </el-form-item>
            </li>
          </ul>

          <DepositDiscount
            v-if="isLoyalty"
            v-bind="depositDiscountProps"
            @setDepositDiscount="setDepositDiscount"
            :discRateChange="getDisRateChange()"
          ></DepositDiscount>

          <div class="form_button">
            <el-button
              class="el-button blue_button"
              :loading="loading"
              :disabled="isDisabled"
              @click="submitCpsForm()"
              data-testid="submit"
            >
              {{ $t('common.button.submit') }}
            </el-button>
          </div>
        </el-form>
        <div v-html="bankHTML"></div>
      </div>
    </div>
  </div>
</template>

<script>
import NumericInput from '@/components/NumericInput';
import AccountNumber from '@/components/form/AccountNumber';
import DepositCurrency from '@/components/form/DepositCurrency';
import { PROMOTION_TYPE } from '@/constants/promotion';
import cpsMixin from '@/mixins/cpsDeposit';
import mixin from '@/mixins/page/deposit';
import { apiExchangeRate } from '@/resource';
import { apiCpsCreditCardpayment } from '@/resource/cps';
import { apiQueryRate_USD_JPY } from '@/resource';
import { validateEmail } from '@/util/validation';
import { countryCodeEnum } from '@/constants/country';
import { paymentSetting } from '@/constants/payment/depositSetting.json';
import DynamicRestrictInput from '@/components/form/DynamicRestrictInput';
import { sensorsTrack } from '@/util/sensorsdata';
import { SENSORS_TRACK } from '@/util/sensorsdata/constant';

export default {
  name: 'CreditCard',
  components: { NumericInput, AccountNumber, DepositCurrency, DynamicRestrictInput },
  mixins: [mixin, cpsMixin],
  data() {
    const validateAmount = (rule, value, callback) => {
      if (value === '' || !Number(value)) {
        callback(new Error(this.$t('common.formValidation.amtReq')));
      } else if (parseFloat(value) < this.minLimit) {
        callback(
          new Error(
            this.$t('common.formValidation.amtLarger', {
              minLimit: this.minLimit,
              currency: this.accountCurrency
            })
          )
        );
      } else if (parseFloat(value) > this.maxLimit) {
        callback(
          new Error(
            this.$t('common.formValidation.amtLess', {
              maxLimit: this.maxLimit,
              currency: this.accountCurrency
            })
          )
        );
      } else {
        callback();
      }
    };
    return {
      bankHTML: '',
      cpsForm: {
        accountNumber: '',
        amount: '',
        depositCurrency: 'USD',
        attachVariables: [],
        mandatory: [],
        notes: '',
        isAgreeImportantNote: false
      },
      supportedDepositCurrencies: ['USD', 'HKD'],
      selectRules: {
        required: true,
        message: this.$t('common.formValidation.common'),
        trigger: 'change'
      },
      inputRules: {
        required: true,
        message: this.$t('common.formValidation.common'),
        trigger: 'blur'
      },
      cpsRules: {
        accountNumber: [
          {
            required: true,
            message: this.$t('common.formValidation.accReq'),
            trigger: 'change'
          }
        ],
        email: [
          {
            required: true,
            message: this.$t('common.formValidation.emailReq'),
            trigger: 'blur'
          },
          {
            validator: validateEmail,
            trigger: 'blur'
          }
        ],
        amount: [
          {
            required: true,
            validator: validateAmount,
            trigger: 'blur'
          }
        ]
      },
      paymentChannel: '',
      exchangeRates: false,
      showExchangeRates: false,
      bankCurrencySupport: false,
      toCurrencyCode: '',
      discountRate: 0.0,
      rate: 0.0,
      minLimit: 50,
      bankList: {},
      formName: 'cpsForm',
      activeIndex: 0,
      isDisabled: false
    };
  },
  computed: {
    instData() {
      return this.getDepositInfo('instData');
    },
    descriptionTitle() {
      return this.langTranslate('deposit.default.channels.', this.paymentDetails.merchant_variable.description_title);
    },
    paymentDetails() {
      return this.$store.state.cps.currentPayment;
    },
    updateMerchantLabel() {
      const merchantLabel = this.$store.state.cps.currentPayment.merchant_variable.label;
      const removeNoise = merchantLabel.replace(/\s/g, '').replace(/-/g, '');
      return `deposit.crypto.${removeNoise}`;
    },
    paymentTypes() {
      let paymentTypes = this.$store.state.cps.payment.child;
      return !paymentTypes ? [] : paymentTypes;
    },
    isMultiplePaymentTypes() {
      return this.paymentTypes.length > 1;
    },
    tradeAccountCurrencies() {
      let arr = [];
      let value = this.paymentDetails.merchant_variable?.trade_account;
      arr = value ? value.split(',') : [];

      return arr;
    },
    rateChange() {
      return (Math.round(this.rate * this.cpsForm.amount * 100) / 100).toFixed(2);
    },
    newCurrencyRate() {
      if (this.paymentDetails.unique_currency) {
        if (this.bankCurrencySupport) {
          return this.cpsForm.depositCurrency;
        } else if (this.accountCurrency === 'USC') {
          return 'USD';
        } else {
          return this.paymentDetails.actual_currency_code;
        }
      }

      return this.paymentDetails.currency_code;
    },
    isEntitleUSDTDepositCashback() {
      return this.$store.state.promotion.eligibleCampaigns.findIndex(
        c => c.campaignType === PROMOTION_TYPE.USDT_DEPOSIT_CASHBACK
      ) > -1
        ? true
        : false;
    },
    countryCode() {
      return parseInt(this.$store.state.common.countryCode);
    },
    isFromDepositFundsCps() {
      return this.$route.query.page === 'special';
    }
  },
  watch: {
    accountCurrency() {
      this.dertermineBankCurrencySupport();
      this.determineExchangeRate();
      this.updateLimit();

      if (this.countryCode === countryCodeEnum.JAPAN && this.accountCurrency === 'JPY' && this.isLoyalty) {
        this.updateDisRate_USD_JPY();
      }
    },
    'cpsForm.depositCurrency': {
      immediate: true,
      handler() {
        this.determineExchangeRate();
      }
    }
  },
  mounted() {
    this.setDefaultPayment();
    sensorsTrack(SENSORS_TRACK.EVENT_NAME.DEPOSIT_DETAIL_PAGE_VIEW);
  },
  methods: {
    setAccountNumber(accountNumber, accountDetail) {
      this.cpsForm.accountNumber = accountNumber;
      this.accountDetail = accountDetail;
    },
    success(msg) {
      window.location = msg;
    },
    elButtonClassName(active) {
      return active ? 'blue_button' : 'white_button';
    },
    submitCpsForm() {
      this.$refs['cpsForm'].validate(valid => {
        if (valid) {
          this.loading = true;
          sensorsTrack(SENSORS_TRACK.EVENT_NAME.DEPOSIT_DETAIL_PAGE_CLICK, {
            account_platform: this.accountDetail.platform,
            account_type: this.accountDetail.accountTypeDisplay,
            account_info: `${this.cpsForm.accountNumber}`,
            account_currency: this.accountCurrency,
            deposit_method: this.paymentDetails.merchant_variable.description_title || '',
            button_name: SENSORS_TRACK.BUTTON_NAME.DEPOSIT_PAY_NOW,
            is_coupon: this.voucherID ? 1 : 0,
            coupon_id: this.voucherID ? `${this.voucherID}` : '',
            coupon_name: this.voucherID ? 'Deposit Discount' : '',
            coupon_amount: this.voucherID ? `${this.discountedAmount}` : '',
            coupon_type: this.voucherID ? 'Deposit Discount' : ''
          });
          this.submitDeposit()
            .then(result => {
              this.cpsHandling(result);
            })
            .catch(err => {
              this.loading = false;
              this.errorMessage(this.$t('deposit.default.failed'));
            });
        } else {
          return false;
        }
      });
    },
    cpsHandling(result) {
      let channelData = JSON.parse(result.data.data);

      if (result.data.code == 0 && result.data.data) {
        this.bankHTML = channelData.payment_content;

        if (this.bankHTML) {
          this.redirectHandling();
        } else {
          this.loading = false;
          this.errorMessage(this.$t('deposit.default.failed'));
        }
      } else {
        this.loading = false;
        if (result.data.code !== 562) {
          this.errorMessage(this.$t('deposit.default.failed'));
        }
      }
    },
    redirectHandling() {
      this.$nextTick(function () {
        if (document.getElementById('paysubmit') != null) {
          if (document.getElementById('paysubmit')) {
            document.getElementById('paysubmit').submit();
          } else {
            this.loading = false;
            this.errorMessage(this.$t('deposit.default.failed'));
          }
        }
      });
    },
    submitDeposit() {
      // Map attach variables
      let attachVariables = {};
      if (this.cpsForm.attachVariables) {
        this.cpsForm.attachVariables.map(a => {
          attachVariables[a.key] = a.value;
        });
      }

      const rawData = this.paymentDetails.attach_variable
        .filter(f => f.field_type)
        .reduce((acc, el) => ({ ...acc, [el.key]: el.value }), {});

      /**
       * When exchange rate conversion is not involved, passing "currency: null" indicates that the backend will use the system's default currency (user MT Account currency)
       * When currency conversion is required, please confirm with the backend whether the 'currency' parameter is required to be sent
       */
      const noNeedCurrencyMethods = [];
      const determineActualCurrencyCode = () => {
        return this.accountCurrency === 'USC' ? 'USD' : this.accountCurrency;
      };
      const getActualCurrency = (item, currency) => {
        if (item.unique_currency && currency) {
          return item.unique_currency.filter(obj => {
            return obj.actual_currency_code === currency;
          })[0].actual_currency_code;
        }
        return item.actual_currency_code;
      };

      return apiCpsCreditCardpayment(
        {
          mtAccount: this.cpsForm.accountNumber,
          operateAmount: this.cpsForm.amount,
          importantNotes: this.cpsForm.notes,
          currency: noNeedCurrencyMethods.includes(this.paymentDetails.payment_method)
            ? null
            : getActualCurrency(this.paymentDetails, determineActualCurrencyCode()),
          attachVariables: JSON.stringify(attachVariables),
          paymentMethod: this.paymentDetails.payment_method,
          redemptionCode: this.voucherID,
          raw: JSON.stringify(rawData)
        },
        this.token
      );
    },
    queryRate() {
      let fromCurrency = this.accountCurrency;

      if (this.bankCurrencySupport && this.paymentDetails.unique_currency) {
        this.toCurrencyCode = this.cpsForm.depositCurrency;
      } else if (this.paymentDetails.unique_currency && this.accountCurrency === 'USC') {
        this.toCurrencyCode = 'USD';
      } else {
        this.toCurrencyCode = this.paymentDetails.currency_code;
      }

      apiExchangeRate({
        fromCurrency: fromCurrency,
        toCurrency: this.toCurrencyCode
      })
        .then(resp => {
          if (resp.data.code == 0) {
            this.rate = resp.data.data;
          }
        })
        .catch(err => {
          this.errorMessage(
            this.$t('deposit.default.rate.result.rateError', {
              oldCurrency: this.accountCurrency,
              newCurrency: this.paymentDetails.actual_currency_code
            })
          );
        });
    },
    determineExchangeRate() {
      // check if exchange rates is required
      if (!this.accountCurrency) {
        return;
      }

      if (this.paymentDetails.unique_currency) {
        if (this.bankCurrencySupport && this.accountCurrency !== this.cpsForm.depositCurrency) {
          this.showExchangeRates = true;
          this.exchangeRates = true;
          this.queryRate();
        } else if (this.accountCurrency === 'USC') {
          this.showExchangeRates = true;
          this.exchangeRates = true;
          this.queryRate();
        } else {
          this.rate = 1;
          this.exchangeRates = false;
          this.showExchangeRates = false;
        }
      } else {
        if (
          (this.accountCurrency != this.paymentDetails.currency_code &&
            !this.cryptoPayment.includes(this.paymentDetails.payment_method)) ||
          (this.cryptoPayment.includes(this.paymentDetails.payment_method) && this.accountCurrency != 'USD')
        ) {
          if (!this.cryptoPayment.includes(this.paymentDetails.payment_method)) {
            this.showExchangeRates = true;
          }
          this.exchangeRates = true;
          this.queryRate();
        } else {
          this.rate = 1;
          this.showExchangeRates = false;
          this.exchangeRates = false;
        }
      }
    },
    handleSelectIndex(index) {
      this.activeIndex = index;

      if (this.paymentTypes.length > 0) {
        let payment = this.paymentTypes[index];
        this.cpsForm.attachVariables = payment.attach_variable
          ? payment.attach_variable.filter(f => f.hasOwnProperty('field_type'))
          : '';
        this.cpsForm.mandatory = payment.attach_variable ? payment.attach_variable.filter(f => f.length > 0)?.[0] : '';
        this.$store.dispatch('cps/actionSetCurrentPayment', payment);

        // Handling after switching tabs
        this.dertermineBankCurrencySupport();
        this.determineExchangeRate();
        this.updateLimit();
      }
    },
    updateLimit() {
      let newLimit = false;

      if (!this.accountCurrency) {
        return;
      }

      if (this.paymentDetails.merchant_variable?.limit) {
        this.paymentDetails.merchant_variable?.limit.some(limit => {
          if (this.accountCurrency == limit.account_currency) {
            this.maxLimit = limit.max;
            this.minLimit = limit.min;
            newLimit = true;
            return true;
          }
        });
      }

      if (!newLimit) {
        this.maxLimit = 99999999.99;
        this.minLimit = 50.0;
      }
    },
    setDepositCurrency(currency) {
      this.cpsForm.depositCurrency = currency;
    },
    dertermineBankCurrencySupport() {
      // Support for Dollarsmart-FPS-USD
      if (this.paymentDetails.payment_method == 'T00600_067') {
        this.bankCurrencySupport = true;
      } else {
        this.bankCurrencySupport = false;
      }
    },
    updateDisRate_USD_JPY() {
      apiQueryRate_USD_JPY()
        .then(resp => {
          if (resp.data.code == 0) this.discountRate = this.initialRate(resp.data.data);
        })
        .catch(err => {
          this.errorMessage(
            this.$t('deposit.default.rate.result.rateError', {
              oldCurrency: this.accountCurrency ? this.accountCurrency : 'USD',
              newCurrency: 'JPY'
            })
          );
        });
    },
    getDisRateChange() {
      let specialExchangeRate = false;

      if (this.countryCode === countryCodeEnum.JAPAN && this.accountCurrency === 'JPY') {
        specialExchangeRate = true;
      }

      if (this.exchangeRates) {
        return {
          currency: this.toCurrencyCode,
          rate: this.rate,
          dp: 2,
          case: 5,
          paymentMethod: this.paymentDetails.payment_method
        };
      } else if (specialExchangeRate) {
        return { rate: this.discountRate, dp: 2, case: 5, paymentMethod: this.paymentDetails.payment_method };
      } else {
        return { rate: this.rate, dp: 2, case: 5, paymentMethod: this.paymentDetails.payment_method };
      }
    },
    getLabelSwitch(merchantVariable) {
      if (typeof merchantVariable.label_switch === 'undefined') return true;
      return merchantVariable.label_switch;
    },
    setDefaultPayment() {
      if (this.paymentTypes.length > 0) {
        //get last selected payment index
        let paymentIndex = this.paymentTypes.findIndex(i => {
          return (
            i.merchant_variable.label == this.paymentDetails.merchant_variable?.label &&
            this.getLabelSwitch(i.merchant_variable)
          );
        });
        // get first available payment if there is no default value
        if (paymentIndex == -1)
          paymentIndex = this.paymentTypes.findIndex(i => {
            return this.getLabelSwitch(i.merchant_variable);
          });
        if (paymentIndex == -1) {
          this.handleSelectIndex(0);
          this.isDisabled = true;
        } else {
          this.activeIndex = paymentIndex;
          this.handleSelectIndex(this.activeIndex);
        }
      }
    },
    getDepositInfo(type) {
      if (!paymentSetting[this.paymentDetails.payment_method]) return;
      let paymentCopywriting = paymentSetting[this.paymentDetails.payment_method].copywriting[type];

      return paymentCopywriting.map(el => {
        let paramsObj = {};
        if (el.obj) paramsObj = el.obj;
        if (el.parameter) {
          Object.entries(el.parameter).forEach(([key, value]) => {
            function getParameter(params, parentValue) {
              parentValue = parentValue[params[0]];
              params.splice(0, 1);
              if (params.length !== 0) parentValue = getParameter(params, parentValue);
              return parentValue;
            }
            paramsObj[key] = getParameter(value.split('.'), this);
          });
        }
        return this.$t(el.value, paramsObj);
      });
    },
    getFieldLocation(attachVariables) {
      let atVariable = attachVariables
        ? attachVariables.filter(f => {
            return f.hasOwnProperty('field_type') && f.field_type !== 'hidden_field';
          })
        : '';
      return atVariable.length % 2 == 0 ? 'fl' : 'fr';
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/pages/deposit/default.scss';
@import '@/assets/css/components/deposit/multiButton.scss';

.mb-2 {
  margin-bottom: 0.5rem;
}

.info_box_choose .is-disabled {
  background-color: $primary-disable;
  color: #fff;
  &:hover {
    background-color: $primary-disable !important;
    color: #fff;
  }
}
.info_box_choose {
  .el-button {
    &:first-of-type {
      @include rtl-sass-prop-sd(margin-left, 10px);
    }
  }
}

.desc_box {
  font-size: 16px;
  margin: 40px 0;

  p:not(:last-child) {
    margin-bottom: 20px;
  }

  > ul {
    > li {
      margin-bottom: 15px;
      width: 100% !important;

      &::marker {
        color: #055bff;
      }
    }
  }

  a {
    color: #90bbea;
    text-decoration: underline;
  }
}

.impt_note_box {
  .imptNotes {
    text-transform: uppercase;
  }
}

@media (max-width: 1200px) {
  .box_card_top.mobile-view {
    padding: 0;

    h4 {
      padding: 24px 24px 0;
      margin-bottom: 0;
      font-size: 16px;
    }

    .info_box {
      padding: 24px;
      font-size: 14px;

      ul {
        @include rtl-sass-prop(padding-left, padding-right, 25px);

        li {
          @include rtl-sass-prop(padding-left, padding-right, 0.5em);
        }
      }
    }

    .img {
      position: relative;
      top: unset;
      @include rtl-sass-prop(right, left, unset);
      text-align: center;
      border-top: solid 1px #cccfd4;
      padding: 10px 0;
    }
  }

  .mobile-view .box_card_bottom {
    padding: 45px 24px 24px;

    /deep/ .form_main ul li {
      width: 100%;

      &.data {
        font-size: 14px;
        color: #00063b;
        margin-bottom: 20px;
      }
    }
  }

  .mobile-view .info_box_choose {
    padding: 24px;
    margin-bottom: 0;

    .el-button {
      min-width: 120px;
    }

    .el-button + .el-button {
      margin-top: 10px;
    }
  }
}
</style>
